import React, { useState } from "react";
import { X, Loader2, User, Mail, Phone, MapPin, Building2 } from "lucide-react";
import API_BASE_URL from "../../../../services/api";


const RefCenterModal = ({ isOpen, onClose, onSave }) => {
  const [form, setForm] = useState({
    name: "",
    contactName: "",
    email: "",
    mobile: "",
    address: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      setError("Center name is required");
      return;
    }

    try {
      setLoading(true);

      const res = await fetch(`${API_BASE_URL}/api/reference-centers`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(form),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data?.message || "Failed to create center");
        return;
      }

      onSave(data.data);   // returns created center to parent
      onClose();          // close modal

      setForm({
        name: "",
        contactName: "",
        email: "",
        mobile: "",
        address: "",
      });

    } catch (err) {
      console.error(err);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white w-full max-w-lg rounded-lg shadow-lg border border-gray-200 animate-fadeIn">
        
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <Building2 className="w-4 h-4 text-indigo-600" />
            Add Reference Center
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">

          {error && (
            <div className="p-2 bg-red-50 border border-red-200 text-xs text-red-700 rounded">
              {error}
            </div>
          )}

          {/* Name */}
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">
              Center Name *
            </label>
            <div className="relative">
              <Building2 className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Enter center name"
                className="w-full pl-8 pr-3 py-2 border rounded text-sm"
                value={form.name}
                onChange={(e) => handleChange("name", e.target.value)}
              />
            </div>
          </div>

          {/* Contact Name */}
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">
              Contact Person
            </label>
            <div className="relative">
              <User className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Contact Person Name"
                className="w-full pl-8 pr-3 py-2 border rounded text-sm"
                value={form.contactName}
                onChange={(e) => handleChange("contactName", e.target.value)}
              />
            </div>
          </div>

          {/* Email + Mobile */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">
                Email
              </label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  placeholder="email@example.com"
                  className="w-full pl-8 pr-3 py-2 border rounded text-sm"
                  value={form.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">
                Mobile
              </label>
              <div className="relative">
                <Phone className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="9876543210"
                  className="w-full pl-8 pr-3 py-2 border rounded text-sm"
                  value={form.mobile}
                  onChange={(e) => handleChange("mobile", e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">
              Address
            </label>
            <div className="relative">
              <MapPin className="w-4 h-4 absolute left-2 top-3 text-gray-400" />
              <textarea
                rows={3}
                className="w-full pl-8 pr-3 py-2 border rounded text-sm resize-none"
                placeholder="Enter full address"
                value={form.address}
                onChange={(e) => handleChange("address", e.target.value)}
              ></textarea>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs border rounded bg-gray-50 hover:bg-gray-100"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2 text-xs text-white bg-indigo-600 rounded hover:bg-indigo-700 flex items-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Center"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RefCenterModal;
